import {
  ANSWER_KEYS,
  type Author,
  type Change,
  type Process,
  type ProcessState,
  type Proposal,
  type Refusal,
  type Step,
  type WritableKey,
  type AnswerKey,
} from "./types";

/**
 * Everything a draft does, as pure functions from one `ProcessState` to the next (Paper CH12, CH12b).
 *
 * A person's edit changes the draft at once and can be undone; Abacor's proposal waits for a person.
 * Both are changes to a named answer against a stated baseline. When two changes land on one answer,
 * the draft keeps neither silently: the proposal becomes a conflict a person decides, and release
 * waits for it. Nothing here reads a clock, so a time is a thing the caller names.
 */

const refuse = (refusal: string): Refusal => ({ refusal });

const findStep = (process: Process, id: string) => process.steps.find((step) => step.id === id);

/** "02": a step's place in the draft, counting across the group. */
export function stepNumber(process: Process, stepId: string): string {
  const index = process.steps.findIndex((step) => step.id === stepId);
  return String(index + 1).padStart(2, "0");
}

export type Resolved = { value: string; source: "written" | "group" | "process" | "derived" };

/** An answer, from the step if it wrote one, else its group, else the process. */
export function resolveAnswer(process: Process, stepId: string, key: AnswerKey): Resolved {
  const step = findStep(process, stepId);
  return step ? resolveFor(process, step, key) : { value: "", source: "process" };
}

/**
 * The same for a step in hand, which may not be in the draft yet: Abacor's proposed step is read
 * against the process it would join.
 */
export function resolveFor(process: Process, step: Step, key: AnswerKey): Resolved {
  if (key === "after") {
    const value =
      step.after.length === 0
        ? step.groupId
          ? "First in group"
          : "First"
        : step.after
            .map((id) => `${stepNumber(process, id)} ${findStep(process, id)?.title ?? ""}`.trim())
            .join(", ");
    return { value, source: "derived" };
  }
  const written = step.answers[key];
  if (written !== undefined && written !== "") return { value: written, source: "written" };
  const group = step.groupId
    ? process.groups.find((candidate) => candidate.id === step.groupId)
    : undefined;
  const fromGroup = group?.answers[key];
  if (fromGroup !== undefined && fromGroup !== "") return { value: fromGroup, source: "group" };
  return { value: process.defaults[key], source: "process" };
}

/** What a step waits for, as the row's label draws it: FIRST, FIRST IN GROUP, AFTER 03 04 06. */
export function afterLabel(process: Process, step: Step): string {
  if (step.after.length === 0) return step.groupId ? "FIRST IN GROUP" : "FIRST";
  return `AFTER ${step.after.map((id) => stepNumber(process, id)).join(" ")}`;
}

/** "2 written · 1 derived · 5 inherited": how a step's eight answers are made up. */
export function answerCounts(process: Process, step: Step) {
  const counts = { written: 0, derived: 0, inherited: 0 };
  for (const key of ANSWER_KEYS) {
    if (key === "after") counts.derived += 1;
    else if (resolveFor(process, step, key).source === "written") counts.written += 1;
    else counts.inherited += 1;
  }
  return counts;
}

/** Capitalized words the process uses mid-sentence, which are names and must stay capitalized. */
export function properNouns(process: Process): ReadonlySet<string> {
  const nouns = new Set<string>();
  const texts = [
    ...Object.values(process.defaults),
    ...process.groups.flatMap((group) => Object.values(group.answers)),
    ...process.steps.flatMap((step) => Object.values(step.answers)),
  ];
  for (const text of texts) {
    for (const sentence of text.split(/(?<=[.!?])\s+/)) {
      sentence
        .split(/\s+/)
        .slice(1)
        .forEach((word) => {
          const clean = word.replace(/[^A-Za-z]/g, "");
          if (/^[A-Z][a-z]+$/.test(clean)) nouns.add(clean);
        });
    }
  }
  return nouns;
}

/** Lowers the first word of an answer to run on from a label, unless it is a name or an acronym. */
export function chipText(value: string, nouns: ReadonlySet<string> = new Set()): string {
  const first = value.split(/\s+/)[0]?.replace(/[^A-Za-z]/g, "") ?? "";
  if (!/^[A-Z][a-z]+$/.test(first) || nouns.has(first)) return value;
  return value.charAt(0).toLowerCase() + value.slice(1);
}

/** The line under a step: "Done when exactly one Xero contact has this ABN." */
export function doneWhenLine(value: string, nouns: ReadonlySet<string> = new Set()): string {
  return `Done when ${chipText(value, nouns)}`;
}

const replaceStep = (state: ProcessState, next: Step): ProcessState => ({
  ...state,
  process: {
    ...state.process,
    steps: state.process.steps.map((step) => (step.id === next.id ? next : step)),
  },
});

const withChange = (state: ProcessState, change: Change): ProcessState => ({
  ...state,
  draft: { ...state.draft, changes: [...state.draft.changes, change] },
});

const nextChangeId = (state: ProcessState, at: Date) =>
  `change-${String(at.getTime())}-${String(state.draft.changes.length)}`;

const setProposal = (
  state: ProcessState,
  id: string,
  update: (proposal: Proposal) => Proposal,
): ProcessState => ({
  ...state,
  draft: {
    ...state.draft,
    proposals: state.draft.proposals.map((proposal) =>
      proposal.id === id ? update(proposal) : proposal,
    ),
  },
});

export type AnswerEdit = {
  stepId: string;
  key: WritableKey;
  value: string;
  by: Author;
  at: Date;
};

/**
 * Writes an answer on a step. An empty value on any answer but Done when clears what the step wrote,
 * so it inherits again. Saying nothing new, or writing what would be inherited anyway, changes nothing.
 */
export function editAnswer(state: ProcessState, edit: AnswerEdit): ProcessState | Refusal {
  const step = findStep(state.process, edit.stepId);
  if (!step) return refuse("That step is not in this draft.");
  const value = edit.value.trim();
  if (edit.key === "doneWhen" && value === "") return refuse("Say when this step is done.");
  const written = step.answers[edit.key];
  const before = written === undefined ? null : written;
  const { [edit.key]: _removed, ...rest } = step.answers;
  let answers = step.answers;
  if (value === "") {
    if (before === null) return state;
    answers = rest;
  } else {
    if (before === value) return state;
    if (before === null && resolveAnswer(state.process, step.id, edit.key).value === value) {
      return state;
    }
    answers = { ...step.answers, [edit.key]: value };
  }
  const change: Change = {
    id: nextChangeId(state, edit.at),
    at: edit.at,
    by: edit.by,
    kind: "answer",
    stepId: step.id,
    key: edit.key,
    before,
    ...(value === "" ? {} : { after: value }),
  };
  return withChange(replaceStep(state, { ...step, answers }), change);
}

export type EditItem = {
  id: string;
  stepId: string;
  key: WritableKey;
  /** What the step wrote before the draft touched it; null if it was inheriting. */
  original: string | null;
  /** What it writes now; null if it inherits again. */
  current: string | null;
  by: Author;
  at: Date;
};

export type AdditionItem = { id: string; stepId: string; by: Author; at: Date };

export type ProposalItem = { proposal: Proposal; conflict: boolean };

/** Whether the answer moved after Abacor proposed against it, so neither change can just win. */
export function conflictOf(state: ProcessState, proposal: Proposal): boolean {
  if (proposal.status !== "pending" || proposal.kind !== "answer") return false;
  if (!proposal.stepId || !proposal.key) return false;
  const current = resolveAnswer(state.process, proposal.stepId, proposal.key).value;
  return current !== proposal.baseline && current !== proposal.value;
}

/** The draft's changes as a person reviews them: one per answer, with where it started. */
export function reviewItems(state: ProcessState) {
  const byField = new Map<string, Change[]>();
  const additions: AdditionItem[] = [];
  for (const change of state.draft.changes) {
    if (change.kind === "step") {
      if (findStep(state.process, change.stepId)) {
        additions.push({ id: change.id, stepId: change.stepId, by: change.by, at: change.at });
      }
      continue;
    }
    const field = `${change.stepId}:${change.key ?? ""}`;
    byField.set(field, [...(byField.get(field) ?? []), change]);
  }
  const edits: EditItem[] = [];
  for (const changes of byField.values()) {
    const first = changes[0];
    const last = changes.at(-1);
    if (!first || !last || !first.key) continue;
    const original = first.before ?? null;
    const current = last.after ?? null;
    if (original === current) continue;
    edits.push({
      id: last.id,
      stepId: last.stepId,
      key: first.key,
      original,
      current,
      by: last.by,
      at: last.at,
    });
  }
  const proposals: ProposalItem[] = state.draft.proposals
    .filter((proposal) => proposal.status === "pending")
    .map((proposal) => ({ proposal, conflict: conflictOf(state, proposal) }));
  return { edits, additions, proposals };
}

export function summarize(state: ProcessState) {
  const { edits, additions, proposals } = reviewItems(state);
  return {
    changes: edits.length + additions.length,
    proposals: proposals.length,
    conflicts: proposals.filter((item) => item.conflict).length,
  };
}

/** Puts an answer back to what it was before the draft touched it, and forgets the edits to it. */
export function revertField(state: ProcessState, stepId: string, key: WritableKey): ProcessState {
  const changes = state.draft.changes.filter(
    (change) => change.kind === "answer" && change.stepId === stepId && change.key === key,
  );
  const first = changes[0];
  const step = findStep(state.process, stepId);
  if (!first || !step) return state;
  const original = first.before ?? null;
  const { [key]: _removed, ...rest } = step.answers;
  const answers = original === null ? rest : { ...step.answers, [key]: original };
  const kept = {
    ...state,
    draft: {
      ...state.draft,
      changes: state.draft.changes.filter((change) => !changes.includes(change)),
    },
  };
  return replaceStep(kept, { ...step, answers });
}

const pendingProposal = (state: ProcessState, id: string) =>
  state.draft.proposals.find((proposal) => proposal.id === id && proposal.status === "pending");

/** Adds the step Abacor proposed, after the step it follows, as it stands now. */
export function addProposedStep(
  state: ProcessState,
  proposalId: string,
  at: Date,
): ProcessState | Refusal {
  const proposal = pendingProposal(state, proposalId);
  if (!proposal || proposal.kind !== "new-step" || !proposal.step || !proposal.afterStepId) {
    return refuse("That proposal is no longer waiting.");
  }
  const index = state.process.steps.findIndex((step) => step.id === proposal.afterStepId);
  if (index < 0) return refuse("The step this follows is no longer in the draft.");
  const after = state.process.steps[index];
  const added: Step = after?.groupId ? { ...proposal.step, groupId: after.groupId } : proposal.step;
  const steps = [
    ...state.process.steps.slice(0, index + 1),
    added,
    ...state.process.steps.slice(index + 1),
  ];
  const next = setProposal(
    { ...state, process: { ...state.process, steps } },
    proposalId,
    (current) => ({ ...current, status: "added" }),
  );
  return withChange(next, {
    id: nextChangeId(state, at),
    at,
    by: "abacor",
    kind: "step",
    stepId: added.id,
  });
}

/** Edits the proposed step's own answers before it is added; the draft is untouched until then. */
export function editProposedStep(
  state: ProcessState,
  proposalId: string,
  key: WritableKey,
  value: string,
): ProcessState | Refusal {
  const proposal = pendingProposal(state, proposalId);
  if (!proposal || proposal.kind !== "new-step" || !proposal.step) {
    return refuse("That proposal is no longer waiting.");
  }
  const trimmed = value.trim();
  if (key === "doneWhen" && trimmed === "") return refuse("Say when this step is done.");
  const { [key]: _removed, ...rest } = proposal.step.answers;
  const answers = trimmed === "" ? rest : { ...proposal.step.answers, [key]: trimmed };
  return setProposal(state, proposalId, (current) =>
    current.step ? { ...current, step: { ...current.step, answers } } : current,
  );
}

export function dismissProposal(state: ProcessState, proposalId: string): ProcessState {
  return setProposal(state, proposalId, (proposal) =>
    proposal.status === "pending" ? { ...proposal, status: "dismissed" } : proposal,
  );
}

/** Applies Abacor's proposed answer as its change. It will not, if the answer moved under it. */
export function acceptProposal(
  state: ProcessState,
  proposalId: string,
  at: Date,
): ProcessState | Refusal {
  const proposal = pendingProposal(state, proposalId);
  if (!proposal || proposal.kind !== "answer" || !proposal.stepId || !proposal.key) {
    return refuse("That proposal is no longer waiting.");
  }
  if (conflictOf(state, proposal)) {
    return refuse(
      "This answer changed after Abacor proposed it. Decide the two changes in the review.",
    );
  }
  return applyProposal(state, proposal, at);
}

function applyProposal(state: ProcessState, proposal: Proposal, at: Date): ProcessState | Refusal {
  if (!proposal.stepId || !proposal.key) return refuse("That proposal is no longer waiting.");
  const edited = editAnswer(state, {
    stepId: proposal.stepId,
    key: proposal.key,
    value: proposal.value ?? "",
    by: "abacor",
    at,
  });
  if ("refusal" in edited) return edited;
  return setProposal(edited, proposal.id, (current) => ({ ...current, status: "added" }));
}

export type Resolution = { kind: "mine" } | { kind: "abacor" } | { kind: "write"; text: string };

/** Decides a conflict: keep your words, take Abacor's, or write the answer by hand. */
export function resolveConflict(
  state: ProcessState,
  proposalId: string,
  how: Resolution,
  at: Date,
): ProcessState | Refusal {
  const proposal = pendingProposal(state, proposalId);
  if (!proposal || !conflictOf(state, proposal))
    return refuse("There is no conflict to decide here.");
  if (how.kind === "mine") return dismissProposal(state, proposalId);
  if (how.kind === "abacor") return applyProposal(state, proposal, at);
  if (!proposal.stepId || !proposal.key) return refuse("That proposal is no longer waiting.");
  const written = editAnswer(state, {
    stepId: proposal.stepId,
    key: proposal.key,
    value: how.text,
    by: "me",
    at,
  });
  if ("refusal" in written) return written;
  return dismissProposal(written, proposalId);
}

/** Release is blocked while a conflict is open, and has nothing to do when nothing changed. */
export function canRelease(state: ProcessState): true | Refusal {
  const { changes, conflicts } = summarize(state);
  if (conflicts > 0) {
    return refuse(
      `${String(conflicts)} ${conflicts === 1 ? "conflict" : "conflicts"} to decide before release`,
    );
  }
  if (changes === 0) return refuse("There is nothing to release yet.");
  return true;
}

/**
 * Where a selection leads now: a step of the draft, a proposal that is waiting, or nothing. A chat
 * links to a proposal by `proposal:<id>` and keeps that link for good, so once the proposal is
 * added the link leads to the step it became, and once it is dismissed or gone it leads nowhere.
 * The canvas follows the answer, so it never holds a selection that draws nothing.
 */
export function resolveFocus(state: ProcessState, selection: string): string | null {
  const isStep = (id: string) => state.process.steps.some((step) => step.id === id);
  if (!selection.startsWith("proposal:")) return isStep(selection) ? selection : null;
  const proposal = state.draft.proposals.find(
    (item) => item.id === selection.slice("proposal:".length),
  );
  // A proposed step has a panel of its own; a proposed answer shows under the answer on its step.
  if (proposal?.status === "pending") {
    return proposal.step
      ? selection
      : proposal.stepId && isStep(proposal.stepId)
        ? proposal.stepId
        : null;
  }
  const became = proposal?.status === "added" ? proposal.step?.id : undefined;
  return became !== undefined && isStep(became) ? became : null;
}

/**
 * Makes the draft the live version and opens the next. What is still proposed stays proposed, and
 * a proposal that was added stays on record, so a chat's link to it still finds the step it became.
 */
export function release(state: ProcessState): ProcessState | Refusal {
  const allowed = canRelease(state);
  if (allowed !== true) return allowed;
  return {
    process: { ...state.process, liveVersion: state.draft.rev },
    draft: {
      rev: state.draft.rev + 1,
      changes: [],
      proposals: state.draft.proposals.filter((proposal) => proposal.status !== "dismissed"),
    },
  };
}

/** What a person can do to a draft, as data, so a screen and the service apply it the same way. */
export type Action =
  | { type: "edit"; stepId: string; key: WritableKey; value: string }
  | { type: "revert"; stepId: string; key: WritableKey }
  | { type: "add-step"; proposalId: string }
  | { type: "edit-proposed"; proposalId: string; key: WritableKey; value: string }
  | { type: "accept"; proposalId: string }
  | { type: "dismiss"; proposalId: string }
  | { type: "resolve"; proposalId: string; how: Resolution }
  | { type: "release" };

/** Applies what a person did, as them, at `at`. */
export function applyAction(state: ProcessState, action: Action, at: Date): ProcessState | Refusal {
  switch (action.type) {
    case "edit":
      return editAnswer(state, { ...action, by: "me", at });
    case "revert":
      return revertField(state, action.stepId, action.key);
    case "add-step":
      return addProposedStep(state, action.proposalId, at);
    case "edit-proposed":
      return editProposedStep(state, action.proposalId, action.key, action.value);
    case "accept":
      return acceptProposal(state, action.proposalId, at);
    case "dismiss":
      return dismissProposal(state, action.proposalId);
    case "resolve":
      return resolveConflict(state, action.proposalId, action.how, at);
    case "release":
      return release(state);
  }
}
